import { useState } from 'react';
import { Link } from 'react-router-dom';
import { trpc } from '../../lib/trpc';
import { PageContainer } from '../../components/PageContainer';
import { PageLoading } from '../../components/ui/LoadingSpinner';
import { ErrorDisplay } from '../../components/ui/ErrorDisplay';
import { ConfirmationModal } from '../../components/ui/ConfirmationModal';
import { Button } from '../../components/ui/Button';
import { Typography } from '../../components/ui/Typography';
import { AdminListPagination } from './AdminListPagination';
import { PurgeUserDialog } from './PurgeUserDialog';
import { daysLeftLabel, reasonLabel } from './accountDeletionCopy';

const PAGE_SIZE = 20;

const headerClass = 'text-xs font-medium text-gray-500 uppercase tracking-wider';

export const AdminPendingDeletions = () => {
  const utils = trpc.useUtils();
  const [page, setPage] = useState(0);
  const [restoreTarget, setRestoreTarget] = useState<{ id: string; email: string } | null>(null);
  const [purgeTarget, setPurgeTarget] = useState<{ id: string; email: string } | null>(null);

  const { data, isLoading, error, refetch } = trpc.adminUsers.pendingDeletions.useQuery({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const refreshAfterChange = (id: string) => {
    utils.adminUsers.pendingDeletions.invalidate();
    utils.adminUsers.bannedEmails.invalidate();
    utils.adminUsers.list.invalidate();
    utils.adminUsers.getById.invalidate({ id });
  };

  const restore = trpc.adminUsers.restore.useMutation({
    onSuccess: (_result, vars) => {
      refreshAfterChange(vars.id);
      setRestoreTarget(null);
    },
    // Close the modal so the page-level error is not hidden behind its backdrop.
    onError: () => setRestoreTarget(null),
  });

  if (isLoading) {
    return (
      <PageContainer>
        <PageLoading message="Loading pending deletions..." />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorDisplay
          title="Failed to load pending deletions"
          message={error.message}
          showRetry
          onRetry={() => refetch()}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        <Typography variant="heading1" as="h1">
          Pending deletions
        </Typography>

        {restore.error && (
          <p role="alert" className="text-sm text-error">
            {restore.error.message}
          </p>
        )}

        <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-x-auto">
          {!data || data.items.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              No accounts are pending deletion.
            </div>
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className={`px-4 py-3 ${headerClass}`}>Email</th>
                  <th className={`px-4 py-3 ${headerClass}`}>Reason</th>
                  <th className={`px-4 py-3 ${headerClass}`}>Deleted by</th>
                  <th className={`px-4 py-3 ${headerClass}`}>Deleted on</th>
                  <th className={`px-4 py-3 ${headerClass}`}>Days left</th>
                  <th className="px-4 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => {
                  const eligible = Date.now() >= new Date(item.purgeEligibleAt).getTime();
                  return (
                    <tr key={item.id} className="border-b border-gray-100 text-sm">
                      <td className="px-4 py-3">
                        <Link
                          to={`/admin/users/${item.id}`}
                          className="text-primary-800 underline-offset-4 hover:underline"
                        >
                          {item.email}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <div>{reasonLabel(item.reason)}</div>
                        {item.note && <div className="text-xs text-gray-500">{item.note}</div>}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{item.deletedBy?.email ?? '—'}</td>
                      <td className="px-4 py-3 text-gray-700">
                        {new Date(item.deletedAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {daysLeftLabel(item.daysRemaining)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setRestoreTarget({ id: item.id, email: item.email })}
                          >
                            Restore
                          </Button>
                          <span
                            title={
                              eligible
                                ? undefined
                                : `Available on ${new Date(item.purgeEligibleAt).toLocaleDateString()}`
                            }
                          >
                            <Button
                              size="sm"
                              variant="dangerGhost"
                              disabled={!eligible}
                              onClick={() => setPurgeTarget({ id: item.id, email: item.email })}
                            >
                              Purge now
                            </Button>
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {data && (
          <AdminListPagination
            page={page}
            pageSize={PAGE_SIZE}
            total={data.total}
            onPageChange={setPage}
          />
        )}
      </div>

      <ConfirmationModal
        isOpen={restoreTarget !== null}
        onClose={() => {
          if (!restore.isPending) setRestoreTarget(null);
        }}
        onConfirm={() => restoreTarget && restore.mutate({ id: restoreTarget.id })}
        title="Restore account"
        message={`${restoreTarget?.email ?? ''}'s content will be visible again and their email unbanned.`}
        confirmText="Restore"
        isLoading={restore.isPending}
      />

      {purgeTarget && (
        <PurgeUserDialog
          isOpen
          userId={purgeTarget.id}
          userEmail={purgeTarget.email}
          onClose={() => setPurgeTarget(null)}
          onPurged={() => {
            refreshAfterChange(purgeTarget.id);
            setPurgeTarget(null);
          }}
        />
      )}
    </PageContainer>
  );
};
